// cSpell:ignore Afrekeningen Bedrag Betaalstatus Betaaluitnodiging Gedeeld Gemeenschappelijk GEMEENSCHAPPELIJK huurder Kost laste Periode Privaat verzonden
// The columns of a cost row (Lewis, 2026-10-16), in this order:
//   Kost | Datum | Type | Periode | Ten laste | Status | Bedrag
// then the row menu. Kost is the cost's title with its supplier under it.
// Type ("Gemeenschappelijk" or "Privaat", the unit's full address in the
// tooltip of "Privaat") is only there inside a building; a list
// across properties shows the property in its place, and a unit on its own
// has neither. "Datum" (the invoice date) and the period are written the way
// the Afrekeningen list below writes its own, and the status is a lozenge as
// its "Betaalstatus huurder" is. Pure, so the order and the texts are
// testable; CostsTable only decorates them (the lozenges, the amount's
// weight).

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { withBilledTarget } from "./chargeTarget.utils";
import { ICostRowLevel, shareCellTexts, TCostLevel } from "./costLevels.utils";
import { costDayText, periodRangeText } from "./costPeriod.utils";
import {
  departureShareLabel,
  ICostSettledInfo,
  isPartlySettledAtDeparture,
} from "./costSettlementState.utils";
import { costListStatus } from "./costsList.utils";
import { TGroupCostUnitShare } from "./groupCostShare.utils";
import { toBrusselsIsoDay } from "./localDay.utils";
import { vatOfAmount } from "./money.utils";
import {
  CATEGORY_LABELS_NL,
  effectivePaidAt,
  Invoice,
  paidCount,
  requestedCount,
  vatPercent,
} from "./types";

export enum ECostListColumn {
  Cost = "cost",
  Type = "type",
  Property = "property",
  Period = "period",
  InvoiceDate = "invoice_date",
  ChargedTo = "charged_to",
  Status = "status",
  Amount = "amount",
}

// The columns of a cost list, in order. Inside a building a Type column
// follows the date; across properties the property takes its place.
export const costListColumns = (params: {
  inBuilding: boolean;
  acrossProperties: boolean;
}): ECostListColumn[] => {
  const { inBuilding, acrossProperties } = params;
  const levelColumns: ECostListColumn[] = [];
  if (inBuilding) levelColumns.push(ECostListColumn.Type);
  else if (acrossProperties) levelColumns.push(ECostListColumn.Property);
  return [
    ECostListColumn.Cost,
    ECostListColumn.InvoiceDate,
    ...levelColumns,
    ECostListColumn.Period,
    ECostListColumn.ChargedTo,
    ECostListColumn.Status,
    ECostListColumn.Amount,
  ];
};

const HEADER_KEYS: Record<ECostListColumn, string> = {
  [ECostListColumn.Cost]: "costs.field.cost",
  [ECostListColumn.Type]: "costs.list.column.type",
  [ECostListColumn.Property]: "costs.field.property",
  [ECostListColumn.Period]: "costs.field.period",
  // The same word as the Afrekeningen list's date column.
  [ECostListColumn.InvoiceDate]: "costs.field.date",
  [ECostListColumn.ChargedTo]: "costs.detail.charged_to",
  // The word the cost's drawer uses for the same status.
  [ECostListColumn.Status]: "costs.detail.status",
  [ECostListColumn.Amount]: "costs.field.amount",
};

export const costListColumnHeader = (column: ECostListColumn): string =>
  getLocalizedText(HEADER_KEYS[column]);

// Where a column's header and cells sit: every column left (Lewis,
// 2026-10-16: the amount too; 2026-10-18: the date, the type and who bears
// the cost too).
export type TCostListAlign = "left" | "center" | "right";

const COLUMN_ALIGN: Record<ECostListColumn, TCostListAlign> = {
  [ECostListColumn.Cost]: "left",
  [ECostListColumn.InvoiceDate]: "left",
  [ECostListColumn.Type]: "left",
  [ECostListColumn.Property]: "left",
  [ECostListColumn.Period]: "left",
  [ECostListColumn.ChargedTo]: "left",
  [ECostListColumn.Status]: "left",
  [ECostListColumn.Amount]: "left",
};

export const costListColumnAlign = (column: ECostListColumn): TCostListAlign =>
  COLUMN_ALIGN[column];

// The room the columns get (between the table's side paddings) on a 1024 px
// and on a 1440 px wide screen.
export const COST_LIST_ROOM_NARROW = 840;
export const COST_LIST_ROOM_WIDE = 1256;

// A width that runs in a straight line from `narrow` px (with
// COST_LIST_ROOM_NARROW of room) to `wide` px (with COST_LIST_ROOM_WIDE), and
// stays at those ends on a smaller or a larger screen. It goes by the
// table's width only, never by a row's content, so every row's grid sizes it
// the same.
export const fluidWidth = (params: { narrow: number; wide: number }) => {
  const { narrow, wide } = params;
  const slope = (wide - narrow) / (COST_LIST_ROOM_WIDE - COST_LIST_ROOM_NARROW);
  const offset = narrow - slope * COST_LIST_ROOM_NARROW;
  const percent = Math.round(slope * 1000000) / 10000;
  const px = Math.round(Math.abs(offset) * 100) / 100;
  return `clamp(${narrow}px, calc(${percent}% ${
    offset < 0 ? "-" : "+"
  } ${px}px), ${wide}px)`;
};

// Widths by what a column holds. Every row is a grid of its own, so a width
// may never depend on a row's content (max-content or auto would size each
// row differently and the columns would not line up under the header): the
// widths are fixed, or follow the table's width (fluidWidth). No column takes
// the room that is left: the table spaces its columns out, so that room goes
// into the gaps, evenly. At a 1024 px wide screen a building page fits within
// the card without a sideways scroll (its columns and smallest gaps take 839
// of 840 px).
// - Kost (the title with the supplier under it) is the widest column, but no
//   wider than a readable line (Lewis, 2026-10-16: 255 px on a 1440 px wide
//   screen, 124 at 1024, a quarter less than before); a longer text is cut
//   with an ellipsis and its tooltip.
// - Datum, Type and Ten laste are just as wide as a date, the lozenge and
//   "Ten laste".
// - Periode ("01/01/2026 → 30/06/2026", 170 px) and Status show their whole
//   text on a wide screen. On a narrow one (Lewis, 2026-10-19) the period is
//   as wide as its first day with the arrow (94 px holds the widest,
//   "08/08/2028 →") and breaks after the arrow, the second day under it, as
//   in the summary; a long status lozenge is cut (its whole text in the
//   tooltip).
// - Bedrag holds the amount and the small lines under it: at 1024 px
//   "€ 123.456,78" (101 px) and a VAT line up to "+ € 2.592,59 btw" (116 px)
//   whole, seven px less than before so the period's first line fits; a
//   longer VAT line is cut.
// Status and Bedrag, by the table's room: the settlement list below shares
// their room between its own Status and Betaalstatus. On a wide screen the
// longest status, "Betaaluitnodiging verzonden" (195 px, French 200 px),
// shows whole (Lewis, 2026-10-21); the room comes out of the gaps, which
// moves Periode and Ten laste to the left.
export const COST_LIST_STATUS_WIDTH = { narrow: 116, wide: 220 };
export const COST_LIST_AMOUNT_WIDTH = { narrow: 117, wide: 140 };

const COLUMN_WIDTHS: Record<ECostListColumn, string> = {
  [ECostListColumn.Cost]: fluidWidth({ narrow: 124, wide: 255 }),
  [ECostListColumn.InvoiceDate]: "76px",
  // "GEMEENSCHAPPELIJK" on one line.
  [ECostListColumn.Type]: "132px",
  [ECostListColumn.Property]: fluidWidth({ narrow: 90, wide: 200 }),
  [ECostListColumn.Period]: fluidWidth({ narrow: 94, wide: 172 }),
  [ECostListColumn.ChargedTo]: "72px",
  [ECostListColumn.Status]: fluidWidth(COST_LIST_STATUS_WIDTH),
  [ECostListColumn.Amount]: fluidWidth(COST_LIST_AMOUNT_WIDTH),
};

export const COST_LIST_MENU_WIDTH = 24;

// The grid's columns: the cost columns, then the row menu.
export const costListGridTemplate = (columns: ECostListColumn[]): string =>
  [
    ...columns.map(column => COLUMN_WIDTHS[column]),
    `${COST_LIST_MENU_WIDTH}px`,
  ].join(" ");

// One column's width, as the grid above gives it.
export const costListColumnWidth = (column: ECostListColumn): string =>
  COLUMN_WIDTHS[column];

const NO_VALUE = "—";

export const costCategoryLabel = (invoice: Invoice): string =>
  invoice.category === "Other" && invoice.categoryOther
    ? invoice.categoryOther
    : CATEGORY_LABELS_NL[invoice.category];

// The cost's title; its category when it has none.
export const costTitle = (invoice: Invoice): string =>
  invoice.description?.trim() || costCategoryLabel(invoice);

// The line under the title: the supplier, nothing without one.
export const costTitleSubLines = (invoice: Invoice): string[] => {
  const supplier = invoice.supplier?.trim();
  return supplier ? [supplier] : [];
};

// Who bears the cost, the way the settlement bills it (withBilledTarget):
// short for the column, with the split for its tooltip.
export const costChargedToTexts = (
  invoice: Invoice,
): { text: string; split: string } => {
  const { target, tenantSharePct } = withBilledTarget(invoice);
  if (target === "owner") {
    return {
      text: getLocalizedText("costs.split.owner"),
      split: getLocalizedText("costs.split.owner_full"),
    };
  }
  if (target === "shared") {
    const tenant = tenantSharePct ?? 0;
    return {
      text: getLocalizedText("costs.split.shared"),
      split: getLocalizedText("costs.split.shared_detail", {
        tenant: String(tenant),
        owner: String(100 - tenant),
      }),
    };
  }
  return {
    text: getLocalizedText("costs.split.tenant"),
    split: getLocalizedText("costs.split.tenant_full"),
  };
};

// The amount column: the cost's amount excl. VAT with "+ € x btw" under it
// when it carries VAT; for a building's cost on a unit page only the unit's
// share, with the whole amount and how the share was found in its tooltip
// (shareCellTexts).
export const costAmountTexts = (params: {
  invoice: Invoice;
  share?: TGroupCostUnitShare;
  // The unit of the page, for the share's tooltip ("bus 2").
  shareUnitLabel?: string;
  format: (amount: number) => string;
}): {
  amount: string;
  subLines: string[];
  isPending: boolean;
  tooltip?: string;
} => {
  const { invoice, share, format } = params;
  if (share) {
    return {
      ...shareCellTexts({
        share,
        amountExclVat: invoice.amountExclVat,
        format,
        unitLabel: params.shareUnitLabel,
      }),
      isPending: share.status === "pending",
    };
  }
  const vat = vatOfAmount({
    amountExclVat: invoice.amountExclVat,
    vatPct: vatPercent(invoice.vatPct),
  });
  return {
    amount: format(invoice.amountExclVat),
    subLines:
      vat !== 0
        ? [
            // A credit note's VAT: "− € 2,10 btw" (M29).
            vat < 0
              ? getLocalizedText("costs.detail.minus_vat", {
                  amount: format(-vat),
                })
              : getLocalizedText("costs.detail.plus_vat", {
                  amount: format(vat),
                }),
          ]
        : [],
    isPending: false,
  };
};

// The Type label's lozenge: the app's standard, plain (not bold) appearances,
// like the other lozenges in these lists. Blue for the building, grey for a
// unit's own cost.
export type TCostTypeAppearance = "info" | "default";

export const costTypeAppearance = (level: TCostLevel): TCostTypeAppearance =>
  level === "building" ? "info" : "default";

// The status lozenge's appearance per state, as the cost list had it before
// the dot: done green, under way blue, waiting on the broker amber.
export type TCostStatusAppearance = "success" | "info" | "warning";

export interface ICostStatusTexts {
  // The lozenge's text; a dash, without a lozenge, for a cost fully for the
  // owner that no settlement holds.
  text: string;
  appearance?: TCostStatusAppearance;
  // Under the lozenge: the day it was paid, sent or settled, or the
  // departures that settled part of the cost.
  subLines: string[];
  // The whole status, for when the column cuts the lozenge short.
  tooltip?: string;
}

const TEXT_SEPARATOR = " · ";

// "16/10/2026" of a calendar day or a moment the API sends.
const displayDay = (value?: string): string | undefined => {
  const day = toBrusselsIsoDay(value);
  return day ? costDayText(day) : undefined;
};

// The day the last payment request of a cost was sent: its splits', or the
// cost's own for a cost from before splits.
const sentAtOf = (invoice: Invoice): string | undefined =>
  (invoice.paymentSplits ?? [])
    .map(split => split.requestedAt)
    .filter((day): day is string => !!day)
    .sort()
    .at(-1) ?? invoice.paymentRequestedAt;

const statusWith = (params: {
  text: string;
  appearance: TCostStatusAppearance;
  subLines?: string[];
  // What the tooltip tells beyond the lines under the lozenge.
  details?: string[];
}): ICostStatusTexts => {
  const subLines = params.subLines ?? [];
  return {
    text: params.text,
    appearance: params.appearance,
    subLines,
    tooltip: [params.text, ...(params.details ?? subLines)].join(
      TEXT_SEPARATOR,
    ),
  };
};

const withDayLine = (value?: string): string[] => {
  const day = displayDay(value);
  return day ? [day] : [];
};

// The Status cell of a cost row. Its state is costListStatus, which the
// status filter reads too, so the column and the filter never disagree.
export const costStatusTexts = (params: {
  // With its live payment status (withLivePaidSplits).
  invoice: Invoice;
  settled: ICostSettledInfo;
}): ICostStatusTexts => {
  const { invoice, settled } = params;
  const splitCount = invoice.paymentSplits?.length ?? 0;
  const status = costListStatus({
    invoice,
    settled: settled.settled,
    partlySettled: isPartlySettledAtDeparture(settled),
  });
  switch (status) {
    case "owner":
      return { text: NO_VALUE, subLines: [] };
    case "paid":
      return statusWith({
        text: getLocalizedText("costs.status.paid"),
        appearance: "success",
        subLines: withDayLine(effectivePaidAt(invoice)),
      });
    case "in_settlement":
      return statusWith({
        text: getLocalizedText("costs.status.in_settlement"),
        appearance: "success",
        subLines: withDayLine(settled.date),
      });
    case "partially_paid":
      return statusWith({
        text: getLocalizedText("costs.status.paid_of_total", {
          paid: String(paidCount(invoice)),
          total: String(splitCount),
        }),
        appearance: "info",
      });
    case "invited":
      return statusWith({
        text: getLocalizedText("costs.status.invitation_sent"),
        appearance: "info",
        subLines: withDayLine(sentAtOf(invoice)),
      });
    case "partially_invited":
      return statusWith({
        text: getLocalizedText("costs.status.invitations_sent_of_total", {
          sent: String(requestedCount(invoice)),
          total: String(splitCount),
        }),
        appearance: "info",
      });
    case "to_request":
      return statusWith({
        text: getLocalizedText("costs.status.to_request"),
        appearance: "warning",
      });
    case "partly_settled": {
      // Under the lozenge only the day, as under every other status: the
      // day the latest departure was confirmed. Who left, and until when, is
      // in the tooltip (Lewis, 2026-10-21).
      const departures = settled.departures ?? [];
      const lastConfirmed = departures
        .map(departure => departure.confirmedAt)
        .filter((day): day is string => !!day)
        .sort()
        .at(-1);
      return statusWith({
        text: getLocalizedText("costs.status.partly_settled_departure"),
        appearance: "warning",
        subLines: withDayLine(lastConfirmed),
        details: [
          ...withDayLine(lastConfirmed),
          ...departures.map(departureShareLabel),
        ],
      });
    }
    case "to_settle":
      return statusWith({
        text: getLocalizedText("costs.status.to_settle"),
        appearance: "warning",
      });
  }
};

export interface ICostListCell {
  column: ECostListColumn;
  text: string;
  // The lozenge of the Type or the Status cell.
  appearance?: TCostTypeAppearance | TCostStatusAppearance;
  // Small secondary lines under the text.
  subLines: string[];
  // The full text behind a short one (the split behind "Gedeeld", the
  // unit's address behind "Privaat").
  tooltip?: string;
  // A building's cost whose share the settlement still has to work out.
  isPending?: boolean;
}

// The texts of one row, column by column.
export const costListRowCells = (params: {
  invoice: Invoice;
  columns: ECostListColumn[];
  rowLevel?: ICostRowLevel;
  propertyLabel?: string;
  share?: TGroupCostUnitShare;
  // The unit of the page, for a share's tooltip ("bus 2").
  shareUnitLabel?: string;
  // Whether a settlement holds the cost, for its Status cell.
  settled?: ICostSettledInfo;
  format: (amount: number) => string;
}): ICostListCell[] => {
  const { invoice, columns, rowLevel, propertyLabel, share, format } = params;
  const settled = params.settled ?? { settled: false };
  const cellOf = (column: ECostListColumn): ICostListCell => {
    switch (column) {
      case ECostListColumn.Cost:
        return {
          column,
          text: costTitle(invoice),
          subLines: costTitleSubLines(invoice),
        };
      case ECostListColumn.Type:
        return {
          column,
          text: rowLevel?.label ?? "",
          appearance: rowLevel && costTypeAppearance(rowLevel.level),
          // Nothing under the label: a private cost's unit is in its tooltip.
          subLines: [],
          tooltip: rowLevel?.unitAddress,
        };
      case ECostListColumn.Property:
        return { column, text: propertyLabel || NO_VALUE, subLines: [] };
      case ECostListColumn.Period:
        return {
          column,
          text: periodRangeText({
            from: invoice.periodFrom,
            to: invoice.periodTo,
          }),
          subLines: [],
        };
      case ECostListColumn.InvoiceDate:
        return { column, text: costDayText(invoice.date), subLines: [] };
      case ECostListColumn.ChargedTo: {
        const { text, split } = costChargedToTexts(invoice);
        return { column, text, subLines: [], tooltip: split };
      }
      case ECostListColumn.Status:
        return { column, ...costStatusTexts({ invoice, settled }) };
      case ECostListColumn.Amount: {
        const { amount, subLines, isPending, tooltip } = costAmountTexts({
          invoice,
          share,
          shareUnitLabel: params.shareUnitLabel,
          format,
        });
        return { column, text: amount, subLines, isPending, tooltip };
      }
    }
  };
  return columns.map(cellOf);
};
